import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { Checkbox, Select, TextInput, Textarea } from '../../components/ui/form';
import { api } from '../../lib/api';
import { useDocumentTitle } from '../../lib/title';
import { uploadDirect, type PresignedTarget } from '../../lib/upload';

/**
 * Sponsorships: what is booked, what it earned, and the form that books one.
 *
 * ## Why this screen shows the numbers next to the form
 *
 * Because the numbers are the product. An advertiser is buying impressions in a
 * slot, and the only version of that anybody can sell is one somebody has
 * measured. Putting the counts on the same screen as the booking means the
 * question "is this slot worth what we charged" is answered where the decision
 * is made, rather than in a report nobody runs.
 *
 * ## House ads are first-class, not a test fixture
 *
 * A slot with nothing in it teaches nobody anything. The house rows - apply to
 * host, drop a track - fill every slot from the day the flag goes on, which
 * means the viewability figures a first advertiser is quoted came from a real
 * slot on a real page rather than from an estimate. `is_house` also keeps them
 * out of the way: a paid campaign always wins the slot outright.
 *
 * ## Pausing is the normal end
 *
 * A finished campaign is switched off and stays here with its count, because
 * that count is the invoice and the case for the renewal. Delete is for the
 * three rows somebody made on the first afternoon.
 */

type Slot = 'dashboard-left' | 'dashboard-feed' | 'room-rail';

interface Campaign {
  id: number;
  name: string;
  advertiser: string;
  slot: Slot;
  image: string | null;
  image_key: string | null;
  alt: string | null;
  headline: string | null;
  body: string | null;
  cta: string | null;
  destination: string;
  starts_at: string | null;
  ends_at: string | null;
  weight: number;
  daily_cap: number;
  is_house: boolean;
  active: boolean;
  created_by: string | null;
  created_at: string;
  impressions: number;
  clicks: number;
  impressions_7d: number;
  clicks_7d: number;
  ctr: number | null;
}

interface Day {
  campaign_id: number;
  day: string;
  slot: Slot;
  impressions: number;
  clicks: number;
}

/** What each slot is, in the words somebody selling one would use. */
const SLOTS: { value: Slot; label: string; note: string }[] = [
  {
    value: 'dashboard-left',
    label: 'Library rail',
    note: 'Under the playlists, down the left of the dashboard. Wide screens only.',
  },
  {
    value: 'dashboard-feed',
    label: 'Dashboard, under new music',
    note: 'The widest one, and the only slot a phone sees.',
  },
  {
    value: 'room-rail',
    label: 'Live room, under chat',
    note: 'The longest anybody sits still on this site.',
  },
];

const BLANK = {
  name: '',
  advertiser: '',
  slot: 'dashboard-feed' as Slot,
  headline: '',
  body: '',
  cta: '',
  destination: '',
  alt: '',
  image_key: '' as string,
  image: null as string | null,
  starts_at: '',
  ends_at: '',
  weight: '100',
  daily_cap: '6',
  is_house: false,
};

type Draft = typeof BLANK;

export function AdminAdsPage() {
  useDocumentTitle('Sponsorships');

  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(BLANK);
  const [editing, setEditing] = useState<number | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['admin', 'ads'],
    queryFn: () => api.get<{ campaigns: Campaign[]; slots: Slot[]; daily: Day[] }>('/admin/ads'),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'ads'] });
    // Whatever is on the screen behind this one is now out of date.
    void queryClient.invalidateQueries({ queryKey: ['ad'] });
  };

  const save = useMutation({
    mutationFn: (values: Draft) => {
      const payload = {
        name: values.name,
        advertiser: values.advertiser,
        slot: values.slot,
        headline: values.headline || null,
        body: values.body || null,
        cta: values.cta || null,
        destination: values.destination,
        alt: values.alt || null,
        image_key: values.image_key || null,
        starts_at: values.starts_at || null,
        ends_at: values.ends_at || null,
        weight: Number(values.weight),
        daily_cap: Number(values.daily_cap),
        is_house: values.is_house,
      };

      return editing === null
        ? api.post('/admin/ads', payload)
        : api.patch(`/admin/ads/${editing}`, payload);
    },
    onSuccess: () => {
      setDraft(BLANK);
      setEditing(null);
      refresh();
    },
  });

  const toggle = useMutation({
    mutationFn: (campaign: Campaign) =>
      api.patch(`/admin/ads/${campaign.id}`, { active: !campaign.active }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/ads/${id}`),
    onSuccess: refresh,
  });

  const campaigns = data?.campaigns ?? [];

  /*
   * The last fortnight per campaign, as a plain array of totals.
   *
   * Built here rather than asked for per row, because the endpoint already
   * returns every day for every campaign in one go and grouping it in the
   * browser is cheaper than fourteen requests that say zero.
   */
  const series = new Map<number, number[]>();

  for (const day of data?.daily ?? []) {
    const existing = series.get(day.campaign_id) ?? [];

    existing.push(day.impressions);
    series.set(day.campaign_id, existing);
  }

  const paid = campaigns.filter((campaign) => !campaign.is_house);
  const house = campaigns.filter((campaign) => campaign.is_house);

  const edit = (campaign: Campaign) => {
    setEditing(campaign.id);
    setDraft({
      name: campaign.name,
      advertiser: campaign.advertiser,
      slot: campaign.slot,
      headline: campaign.headline ?? '',
      body: campaign.body ?? '',
      cta: campaign.cta ?? '',
      destination: campaign.destination,
      alt: campaign.alt ?? '',
      image_key: campaign.image_key ?? '',
      image: campaign.image,
      // `datetime-local` will not accept seconds-with-a-space, which is what
      // MySQL hands back.
      starts_at: local(campaign.starts_at),
      ends_at: local(campaign.ends_at),
      weight: String(campaign.weight),
      daily_cap: String(campaign.daily_cap),
      is_house: campaign.is_house,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Sponsorships</h1>
        <p className="max-w-2xl text-sm leading-relaxed text-soft">
          Three slots, no third-party ad tech, and no cookies. The creative is an image in our own
          bucket and the click is a redirect through our own API. Nothing is shown to anybody until
          the <strong className="font-medium text-ink">Sponsorships</strong> feature is switched on.
        </p>
      </header>

      <Editor
        draft={draft}
        editing={editing}
        onChange={setDraft}
        onCancel={() => {
          setDraft(BLANK);
          setEditing(null);
        }}
        onSave={() => save.mutate(draft)}
        saving={save.isPending}
        error={save.isError}
      />

      <Card className="flex flex-col gap-4 p-5">
        <h2 className="meta-caps text-faint">Booked</h2>

        {isPending ? (
          <SkeletonRow />
        ) : paid.length === 0 ? (
          <p className="text-sm text-soft">Nothing booked. The house ads below fill the slots.</p>
        ) : (
          <ul className="flex flex-col gap-2" data-ads-paid>
            {paid.map((campaign) => (
              <Row
                key={campaign.id}
                campaign={campaign}
                series={series.get(campaign.id) ?? []}
                onEdit={() => edit(campaign)}
                onToggle={() => toggle.mutate(campaign)}
                onDelete={() => remove.mutate(campaign.id)}
              />
            ))}
          </ul>
        )}
      </Card>

      <Card className="flex flex-col gap-4 p-5">
        <h2 className="meta-caps text-faint">Ours</h2>
        <p className="text-xs leading-relaxed text-faint">
          Shown only when no paid campaign is eligible for the slot, so a slot is never empty and a
          paid one is never diluted.
        </p>

        {house.length === 0 ? (
          <p className="text-sm text-soft">None yet.</p>
        ) : (
          <ul className="flex flex-col gap-2" data-ads-house>
            {house.map((campaign) => (
              <Row
                key={campaign.id}
                campaign={campaign}
                series={series.get(campaign.id) ?? []}
                onEdit={() => edit(campaign)}
                onToggle={() => toggle.mutate(campaign)}
                onDelete={() => remove.mutate(campaign.id)}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* One campaign in the list                                                    */
/* -------------------------------------------------------------------------- */

function Row({
  campaign,
  series,
  onEdit,
  onToggle,
  onDelete,
}: {
  campaign: Campaign;
  /** Impressions per day for the last fortnight, oldest first. */
  series: number[];
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const slot = SLOTS.find((option) => option.value === campaign.slot);

  return (
    <li
      className="flex flex-wrap items-center gap-3 rounded-[var(--radius-control)] border border-line p-3"
      data-ad-row={campaign.id}
    >
      {campaign.image !== null && (
        <img
          src={campaign.image}
          alt=""
          className="h-12 w-20 shrink-0 rounded-[var(--radius-chip)] object-cover"
        />
      )}

      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-ink">{campaign.name}</span>
        <span className="meta-caps text-faint">
          {campaign.advertiser} · {slot?.label ?? campaign.slot}
        </span>
      </div>

      {/*
        Lifetime first, because that is the invoice. The week beside it because
        a flat zero for three days is the only way to notice a slot has quietly
        stopped serving.
      */}
      {/*
        A fortnight of impressions, as bars.

        Not a chart with axes - there is nothing to read off it. It answers one
        question, which is the one the totals cannot: is this still serving.
        A column of zeros beside a healthy lifetime figure is a campaign that
        stopped, and that is invisible in any single number.
      */}
      {series.length > 0 && (
        <div className="ml-auto flex h-8 shrink-0 items-end gap-[2px]" data-ad-series aria-hidden>
          {series.map((value, index) => (
            <span
              key={index}
              title={`${value}`}
              className="w-[3px] rounded-t-[1px] bg-line-strong"
              style={{
                height: `${Math.max(6, (value / Math.max(...series)) * 100)}%`,
              }}
            />
          ))}
        </div>
      )}

      <div className={`flex shrink-0 items-center gap-5 font-mono text-[11px] text-soft ${series.length > 0 ? '' : 'ml-auto'}`}>
        <span title="Impressions, all time">
          {campaign.impressions.toLocaleString()} <span className="text-faint">seen</span>
        </span>
        <span title="Clicks, all time">
          {campaign.clicks.toLocaleString()} <span className="text-faint">clicks</span>
        </span>
        <span title="Click-through rate" className="text-faint">
          {campaign.ctr === null ? '—' : `${campaign.ctr}%`}
        </span>
        <span title="Last seven days" className="text-ghost">
          7d {campaign.impressions_7d.toLocaleString()}
        </span>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {campaign.active ? <Chip tone="signal">Live</Chip> : <Chip>Paused</Chip>}

        <Button variant="ghost" onClick={onEdit} data-ad-edit>
          Edit
        </Button>
        <Button variant="secondary" onClick={onToggle} data-ad-toggle>
          {campaign.active ? 'Pause' : 'Resume'}
        </Button>
        <Button
          variant="ghost"
          data-ad-delete
          onClick={() => {
            if (window.confirm(`Delete ${campaign.name} and its counts?`)) onDelete();
          }}
        >
          Delete
        </Button>
      </div>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* The form                                                                    */
/* -------------------------------------------------------------------------- */

function Editor({
  draft,
  editing,
  onChange,
  onCancel,
  onSave,
  saving,
  error,
}: {
  draft: Draft;
  editing: number | null;
  onChange: (next: Draft) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: boolean;
}) {
  const input = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => onChange({ ...draft, [key]: value });

  /**
   * Sign, PUT, confirm - and the confirm resizes on the spot.
   *
   * Unlike every other image on the platform, which queues a job: there is no
   * campaign row yet to point at a picture that is still being made, so the
   * form waits the second rather than polling for a key it invented.
   */
  async function upload(file: File) {
    setUploadError(null);
    setUploading(true);

    try {
      const target = await api.post<PresignedTarget>('/admin/ads/creative/init', {
        content_type: file.type,
        size_bytes: file.size,
      });

      await uploadDirect(target, file);

      const done = await api.post<{ key: string; url: string }>('/admin/ads/creative', {
        key: target.key,
      });

      onChange({ ...draft, image_key: done.key, image: done.url });
    } catch (caught) {
      setUploadError(caught instanceof Error ? caught.message : 'That image did not upload.');
    } finally {
      setUploading(false);
    }
  }

  const slot = SLOTS.find((option) => option.value === draft.slot);

  return (
    <Card className="flex flex-col gap-5 p-5" data-ad-editor>
      <h2 className="meta-caps text-faint">{editing === null ? 'Book a slot' : 'Editing'}</h2>

      <div className="grid gap-4 sm:grid-cols-2">
        <Labelled label="Name" hint="Internal. Never shown to a reader.">
          <TextInput
            value={draft.name}
            data-ad-name
            onChange={(event) => set('name', event.target.value)}
          />
        </Labelled>

        <Labelled label="Advertiser" hint="Shown on the card, beside the Sponsored label.">
          <TextInput
            value={draft.advertiser}
            data-ad-advertiser
            onChange={(event) => set('advertiser', event.target.value)}
          />
        </Labelled>

        <Labelled label="Slot" hint={slot?.note}>
          <Select
            value={draft.slot}
            data-ad-slot
            options={SLOTS.map((option) => ({ value: option.value, label: option.label }))}
            onChange={(value) => set('slot', value as Slot)}
          />
        </Labelled>

        <Labelled
          label="Destination"
          hint="https://… or a path on this site starting with /. Clicks redirect through us."
        >
          <TextInput
            value={draft.destination}
            placeholder="https://"
            data-ad-destination
            onChange={(event) => set('destination', event.target.value)}
          />
        </Labelled>

        <Labelled label="Headline">
          <TextInput
            value={draft.headline}
            maxLength={80}
            data-ad-headline
            onChange={(event) => set('headline', event.target.value)}
          />
        </Labelled>

        <Labelled label="Call to action" hint="A couple of words. “Listen”, “Get tickets”.">
          <TextInput
            value={draft.cta}
            maxLength={40}
            data-ad-cta
            onChange={(event) => set('cta', event.target.value)}
          />
        </Labelled>
      </div>

      <Labelled label="Body">
        <Textarea
          rows={2}
          value={draft.body}
          maxLength={200}
          data-ad-body
          onChange={(event) => set('body', event.target.value)}
        />
      </Labelled>

      {/* ── The picture ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-4 border-t border-line pt-4">
        {draft.image !== null && draft.image !== '' && (
          <img
            src={draft.image}
            alt=""
            className="h-16 w-28 shrink-0 rounded-[var(--radius-chip)] object-cover"
            data-ad-preview
          />
        )}

        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="sr-only"
          data-ad-file
          onChange={(event) => {
            const file = event.target.files?.[0];

            if (file) void upload(file);
          }}
        />

        <Button variant="secondary" loading={uploading} onClick={() => input.current?.click()}>
          {draft.image ? 'Replace image' : 'Add an image'}
        </Button>

        {draft.image && (
          <Button variant="ghost" onClick={() => onChange({ ...draft, image_key: '', image: null })}>
            Remove
          </Button>
        )}

        <div className="min-w-[14rem] flex-1">
          <Labelled label="Alt text" hint="What the picture says, for somebody who cannot see it.">
            <TextInput
              value={draft.alt}
              maxLength={200}
              data-ad-alt
              onChange={(event) => set('alt', event.target.value)}
            />
          </Labelled>
        </div>
      </div>

      {uploadError !== null && <Alert tone="error">{uploadError}</Alert>}

      {/* ── The flight ────────────────────────────────────────────────── */}
      <div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-4">
        <Labelled label="Starts" hint="Blank starts now.">
          <TextInput
            type="datetime-local"
            value={draft.starts_at}
            data-ad-starts
            onChange={(event) => set('starts_at', event.target.value)}
          />
        </Labelled>

        <Labelled label="Ends" hint="Blank runs until paused.">
          <TextInput
            type="datetime-local"
            value={draft.ends_at}
            data-ad-ends
            onChange={(event) => set('ends_at', event.target.value)}
          />
        </Labelled>

        <Labelled label="Weight" hint="Share of the slot against others in it.">
          <TextInput
            type="number"
            min={1}
            max={1000}
            value={draft.weight}
            data-ad-weight
            onChange={(event) => set('weight', event.target.value)}
          />
        </Labelled>

        <Labelled label="Times a day, per reader" hint="0 for no cap.">
          <TextInput
            type="number"
            min={0}
            max={100}
            value={draft.daily_cap}
            data-ad-cap
            onChange={(event) => set('daily_cap', event.target.value)}
          />
        </Labelled>
      </div>

      <Checkbox
        checked={draft.is_house}
        data-ad-house
        label="Ours"
        hint="Only shown when no paid campaign is eligible for the slot."
        onChange={(event) => set('is_house', event.target.checked)}
      />

      <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4">
        <Button
          loading={saving}
          data-ad-save
          disabled={
            draft.name.trim() === '' ||
            draft.advertiser.trim() === '' ||
            draft.destination.trim() === ''
          }
          onClick={onSave}
        >
          {editing === null ? 'Book it' : 'Save changes'}
        </Button>

        {editing !== null && (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}

        {error && <span className="meta text-negative">That did not save.</span>}
      </div>
    </Card>
  );
}

function Labelled({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-soft">{label}</span>
      {children}
      {hint !== undefined && <span className="text-xs leading-relaxed text-faint">{hint}</span>}
    </label>
  );
}

/**
 * `2026-09-07 18:00:00.000` as `2026-09-07T18:00`.
 *
 * `datetime-local` refuses anything else, silently - it clears itself and the
 * form looks like it lost the date rather than like it could not read it.
 */
function local(value: string | null): string {
  if (value === null) return '';

  return value.replace(' ', 'T').slice(0, 16);
}
